import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle } from '../components/adminTheme';
import { useLang } from '../useLang';

function Posts() {
    const navigate = useNavigate();
    const { t } = useLang();
    const [posts, setPosts] = useState([]);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [deleteModal, setDeleteModal] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => { document.title = 'Posts — winston.ie Studio'; }, []);

    useEffect(() => {
        const checkUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) { navigate('/login'); return; } const { data: userData } = await supabase
                .from('users')
                .select('username, role, avatar_url')
                .eq('email', data.session?.user?.email)
                .single();
            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAvatarUrl(userData.avatar_url || '');
                fetchPosts(userData);
            }
        };
        checkUser();
    }, [navigate]);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const fetchPosts = async (userData) => {
        let query = supabase.from('posts').select('*').order('created_at', { ascending: false });
        if (userData.role !== 'ADMIN') {
            query = query.or(`author_username.eq.${userData.username},co_authors.cs.{${userData.username}}`);
        }
        const { data } = await query;
        if (data) setPosts(data);
    };

    const handleDelete = async () => {
        const { data } = await supabase.auth.getSession();
        const email = data.session?.user?.email;
        const { error } = await supabase.auth.signInWithPassword({ email, password: deletePassword });
        if (error) { setDeleteError(t('wrongPassword')); return; }
        await supabase.from('posts').delete().eq('slug', deleteModal);
        setPosts(posts.filter(p => p.slug !== deleteModal));
        setDeleteModal(null);
        setDeletePassword('');
        setDeleteError('');
    };

    const byline = (post) => {
        const handles = [post.author_username, ...(post.co_authors || [])].filter(Boolean);
        return handles.map(h => `@${h}`).join(' & ');
    };

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .cposts-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    max-width: 920px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cposts-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .cposts-row {
                    border: 1px solid ${theme.border};
                    background: ${theme.card};
                    box-shadow: 0 1px 3px rgba(14,16,19,0.04);
                    padding: 16px 18px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    transition: border-color .2s ease, box-shadow .2s ease;
                }
                .cposts-row:hover { border-color: ${theme.borderHover}; box-shadow: 0 4px 14px rgba(14,16,19,0.06); }
            `}</style>

            <AdminNav username={username} role={role} avatarUrl={avatarUrl} onLogout={handleLogout} activePath="/admin/posts" />

            <div className="cposts-content">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <p style={{ fontFamily: font, fontWeight: 700, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.textMuted, margin: '0 0 8px' }}>
                            {t('posts')}
                        </p>
                        <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>
                            {t('yourArticles')}
                        </h1>
                    </div>
                    <button className="wlink" style={pillButton} onClick={() => navigate('/admin/new-post')}>+ {t('newPost')}</button>
                </div>

                {posts.length === 0 ? (
                    <p style={{ fontFamily: font, fontWeight: 500, fontSize: '13px', color: theme.textMuted }}>{t('noPostsYet')}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {posts.map((post) => (
                            <div key={post.slug} className="cposts-row">
                                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', minWidth: 0 }}>
                                    {post.cover_image && (
                                        <img src={post.cover_image} alt={post.title} style={{ width: '56px', height: '56px', objectFit: 'cover', flexShrink: 0 }} />
                                    )}
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontFamily: font, fontSize: '13.5px', fontWeight: 700, color: theme.text, marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {post.title}
                                        </div>
                                        <div style={{ fontFamily: font, fontSize: '11px', fontWeight: 500, color: theme.textMuted }}>
                                            {byline(post)} · {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '16px', flexShrink: 0, alignItems: 'center' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => navigate(`/admin/edit-post/${post.slug}`)}>{t('edit')}</button>
                                    <button className="wlink" style={outlineButton} onClick={() => navigate(`/blog/${post.slug}`)}>{t('view')}</button>
                                    {(role === 'ADMIN' || post.author_username === username || (post.co_authors || []).includes(username)) && (
                                        <button
                                            onClick={() => setDeleteModal(post.slug)}
                                            className="wlink"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: '12px', fontWeight: 700, color: theme.danger }}
                                        >
                                            {t('delete')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {deleteModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 }}>
                    <div style={{ background: theme.card, padding: '32px', width: 'min(320px, calc(100vw - 48px))', boxSizing: 'border-box' }}>
                        <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>{t('deletePost')}</h3>
                        <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, marginBottom: '20px' }}>{t('deleteConfirm')}</p>
                        <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t('password')} style={{ ...inputStyle, marginBottom: '8px' }} />
                        {deleteError && <p style={{ color: theme.danger, fontFamily: font, fontSize: '12px', fontWeight: 500, marginBottom: '8px' }}>{deleteError}</p>}
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
                            <button className="wlink" style={outlineButton} onClick={() => { setDeleteModal(null); setDeletePassword(''); setDeleteError(''); }}>{t('cancel')}</button>
                            <button className="wlink" style={pillButton} onClick={handleDelete}>{t('delete')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Posts;